import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge } from '../components/Badge'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { Modal } from '../components/Modal'
import { ToastProvider, useToast } from '../components/Toast'
import { useDb } from '../state/DbProvider'
import {
  agenciesForSuperAgency,
  dealsVisibleToPartner,
  findProduct,
  findUser,
  getAssignedAgencyForDeal,
  getCurrentUser,
  getDealParticipants,
  isAgencyLikeRole,
} from '../state/selectors'
import type { Deal, DealStatus, Product, User } from '../state/types'
import { formatDateYMD, formatJPY, formatPercent } from '../utils/format'
import { getProductPriceLabel } from '../utils/products'
import {
  calculateRewardBreakdown,
  getEffectiveAgencyRateForProduct,
  getEffectiveConnectorRateForProduct,
  getEffectiveSuperAgencyRate,
} from '../utils/reward'

function statusFlow(productType: Product['type']): DealStatus[] {
  if (productType === 'hotel_membership') return ['申し込み', '失注']
  if (productType === 'ad_slot') return ['申し込み', '審査', '失注']
  return ['リード発生', '商談中', '契約締結', '失注']
}

function completionLabel(productType: Product['type']): string {
  if (productType === 'hotel_membership') return '決済完了（売上確定）'
  if (productType === 'ad_slot') return '掲載開始（売上確定）'
  return '施工完了（売上確定）'
}

function labelForAssignableAgency(agency: User, me: User): string {
  if (agency.id === me.id && me.role === '総代理店') return `${agency.name}（総代理店直案件）`
  return agency.name
}

function DealsInner() {
  const { db, actions } = useDb()
  const toast = useToast()
  const location = useLocation()
  const navigate = useNavigate()

  const [showCreate, setShowCreate] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState('')
  const [selectedAssignedAgencyId, setSelectedAssignedAgencyId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [memo, setMemo] = useState('')
  const [finalizeDealId, setFinalizeDealId] = useState<string | null>(null)

  const products = useMemo(
    () => (db ? db.products.filter((product) => product.isPublic).sort((a, b) => a.name.localeCompare(b.name, 'ja')) : []),
    [db],
  )
  const publicProductIds = useMemo(() => new Set(products.map((product) => product.id)), [products])

  useEffect(() => {
    if (!db) return

    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('openCreate') !== '1') return

    const requestedProductId = searchParams.get('productId') ?? ''
    setSelectedProductId(publicProductIds.has(requestedProductId) ? requestedProductId : '')
    setShowCreate(true)
    navigate({ pathname: location.pathname }, { replace: true })
  }, [db, location.pathname, location.search, navigate, publicProductIds])

  if (!db) return null
  const me = getCurrentUser(db)

  useEffect(() => {
    if (!isAgencyLikeRole(me.role)) return
    setSelectedAssignedAgencyId((current) => current || me.id)
  }, [me.id, me.role])

  if (!isAgencyLikeRole(me.role) && me.role !== 'コネクター') {
    return (
      <Card title="商談管理" subtitle="このページは総代理店 / 代理店 / コネクター向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const canManage = isAgencyLikeRole(me.role)
  const myDeals =
    me.role === 'コネクター'
      ? db.deals.filter((deal) => deal.connectorId === me.id)
      : dealsVisibleToPartner(db, me.id)

  const visibleDeals = myDeals.filter((deal) => deal.status !== '受付待ち')
  const openDeals = visibleDeals.filter((deal) => !deal.locked)
  const closedDeals = visibleDeals.filter((deal) => deal.locked)
  const finalizeTarget = visibleDeals.find((deal) => deal.id === finalizeDealId) ?? null
  const finalizeProduct = finalizeTarget ? findProduct(db, finalizeTarget.productId) : undefined
  const assignableAgencies =
    me.role === '総代理店'
      ? [me, ...agenciesForSuperAgency(db, me.id)]
      : me.role === '代理店'
        ? [me]
        : []

  return (
    <div className="grid gap-4">
      <Card
        title="商談管理"
        subtitle={
          me.role === '総代理店'
            ? '直属コネクター案件、総代理店直案件、配下代理店案件を管理し、ステータス更新と売上確定を行います。'
            : canManage
              ? '配下コネクター案件と代理店直案件を管理し、ステータス更新と売上確定を行います。'
              : '自分が登録した案件の顧客情報とステータスを閲覧できます。'
        }
        right={
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            商談登録
          </Button>
        }
      >
        <div className="px-5 py-4 text-sm text-slate-600">
          未確定 {openDeals.length} 件 / 売上確定済み {closedDeals.length} 件
        </div>
      </Card>

      <Card title="進行中の商談" subtitle={canManage ? 'ステータス更新と売上確定ができます。' : 'コネクターは閲覧のみです。'}>
        <div className="grid gap-3 px-5 py-4">
          {openDeals.length === 0 ? (
            <div className="text-sm text-slate-600">進行中の商談はありません。</div>
          ) : (
            openDeals
              .slice()
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
              .map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  showConnector={canManage}
                  showAssignedAgency={me.role === '総代理店'}
                  onUpdateStatus={
                    canManage
                      ? async (nextStatus) => {
                          const result = await actions.updateDealStatus(deal.id, nextStatus)
                          if (!result.ok) {
                            toast.show(result.message ?? 'ステータスを更新できませんでした', 'error')
                          }
                        }
                      : undefined
                  }
                  onFinalize={canManage ? () => setFinalizeDealId(deal.id) : undefined}
                />
              ))
          )}
        </div>
      </Card>

      <Card title="売上確定済み" subtitle="売上確定後の商談一覧です。">
        <div className="grid gap-3 px-5 py-4">
          {closedDeals.length === 0 ? (
            <div className="text-sm text-slate-600">売上確定済みの商談はありません。</div>
          ) : (
            closedDeals
              .slice()
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
              .map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  showConnector={canManage}
                  showAssignedAgency={me.role === '総代理店'}
                />
              ))
          )}
        </div>
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="商談登録">
        <div className="grid gap-4">
          <label className="grid gap-1">
            <span className="text-sm text-slate-700">商材</span>
            <select
              value={selectedProductId}
              onChange={(event) => setSelectedProductId(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">選択してください</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} / {getProductPriceLabel(product)}
                </option>
              ))}
            </select>
          </label>

          {me.role === '総代理店' ? (
            <label className="grid gap-1">
              <span className="text-sm text-slate-700">案件の帰属先</span>
              <select
                value={selectedAssignedAgencyId}
                onChange={(event) => setSelectedAssignedAgencyId(event.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                {assignableAgencies.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {labelForAssignableAgency(agency, me)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {me.role === '総代理店'
              ? '総代理店は自社直案件として登録するか、配下代理店案件として帰属先を選んで登録できます。'
              : me.role === '代理店'
                ? '代理店が直接登録した案件は、担当コネクターなしで自社案件として登録されます。'
                : 'コネクターが登録した案件は、所属先へ自動的に割り当てられます。'}
          </div>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">顧客 会社名</span>
            <input
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="個人の場合は空欄可"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">顧客 担当者名</span>
            <input
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">顧客 メールアドレス</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">顧客 電話番号</span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">備考</span>
            <textarea
              value={memo}
              onChange={(event) => setMemo(event.target.value)}
              className="min-h-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            />
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              キャンセル
            </Button>
            <Button
              variant="primary"
              disabled={
                !selectedProductId
                || !contactName.trim()
                || !email.trim()
                || (me.role === '総代理店' && !selectedAssignedAgencyId)
              }
              onClick={async () => {
                const result = await actions.createDealManual({
                  productId: selectedProductId,
                  customerCompanyName: companyName,
                  customerName: contactName,
                  customerEmail: email,
                  customerPhone: phone,
                  memo,
                  assignedAgencyId: me.role === '総代理店' ? selectedAssignedAgencyId : undefined,
                })

                if (!result.ok) {
                  toast.show(result.message ?? '商談を登録できませんでした', 'error')
                  return
                }

                toast.show('商談を登録しました', 'success')
                setShowCreate(false)
                setSelectedProductId('')
                setSelectedAssignedAgencyId(isAgencyLikeRole(me.role) ? me.id : '')
                setCompanyName('')
                setContactName('')
                setEmail('')
                setPhone('')
                setMemo('')
              }}
            >
              登録する
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!finalizeTarget && !!finalizeProduct}
        onClose={() => setFinalizeDealId(null)}
        title={finalizeProduct ? completionLabel(finalizeProduct.type) : '売上確定'}
      >
        {finalizeTarget && finalizeProduct ? (
          <FinalizeForm deal={finalizeTarget} product={finalizeProduct} onClose={() => setFinalizeDealId(null)} />
        ) : null}
      </Modal>
    </div>
  )
}

function DealRow(props: {
  deal: Deal
  showConnector: boolean
  showAssignedAgency?: boolean
  onUpdateStatus?: (status: DealStatus) => void
  onFinalize?: () => void
}) {
  const { db } = useDb()
  if (!db) return null

  const product = findProduct(db, props.deal.productId)
  const connector = findUser(db, props.deal.connectorId)
  const assignedAgency = getAssignedAgencyForDeal(db, props.deal)
  const nextStatuses = product ? statusFlow(product.type).filter((status) => status !== props.deal.status) : []

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-slate-900">
            {product?.name ?? '商品不明'}
            <span className="ml-2 text-xs font-normal text-slate-500">{getProductPriceLabel(product ?? { listPriceJPY: 0 } as Product)}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            登録日: {formatDateYMD(props.deal.createdAt)} / ステータス: {props.deal.status}
            {props.deal.locked && props.deal.closingDate ? ` / 売上確定日: ${props.deal.closingDate}` : ''}
          </div>
          <div className="mt-2 text-sm text-slate-700">
            {props.deal.customerCompanyName || '会社名未入力'} / {props.deal.customerName} / {props.deal.customerEmail}
            {props.deal.customerPhone ? ` / ${props.deal.customerPhone}` : ''}
          </div>
          {props.showAssignedAgency ? (
            <div className="mt-1 text-xs text-slate-600">担当先: {assignedAgency?.name ?? 'なし'}</div>
          ) : null}
          {props.showConnector ? (
            <div className="mt-1 text-xs text-slate-600">担当コネクター: {connector?.name ?? 'なし'}</div>
          ) : null}
          {props.deal.memo ? <div className="mt-3 text-sm text-slate-700">備考: {props.deal.memo}</div> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {props.deal.locked ? <Badge tone="green">売上確定済み</Badge> : <Badge tone="amber">進行中</Badge>}

          {!props.deal.locked && props.onUpdateStatus ? (
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              defaultValue=""
              onChange={(event) => {
                if (!event.target.value) return
                props.onUpdateStatus?.(event.target.value as DealStatus)
                event.currentTarget.value = ''
              }}
            >
              <option value="">ステータス更新</option>
              {nextStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          ) : null}

          {!props.deal.locked && props.onFinalize ? (
            <Button variant="primary" onClick={props.onFinalize}>
              売上確定
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function FinalizeForm(props: { deal: Deal; product: Product; onClose: () => void }) {
  const { db, actions } = useDb()
  const toast = useToast()

  const [closingDate, setClosingDate] = useState<string>(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  })

  const me = db ? getCurrentUser(db) : null
  if (!db || !me) return null

  const { connector, assignedAgency, superAgency } = getDealParticipants(db, props.deal)
  const assignedAgencyRole = assignedAgency?.role === '総代理店' ? '総代理店' : '代理店'
  const agencyRate = assignedAgency?.role === '代理店'
    ? getEffectiveAgencyRateForProduct(props.product, db.settings, assignedAgency)
    : getEffectiveAgencyRateForProduct(props.product, db.settings)
  const connectorRate = getEffectiveConnectorRateForProduct(props.product, db.settings, connector)
  const superAgencyRate = getEffectiveSuperAgencyRate(db.settings)
  const hasConnector = !!connector
  const saleAmount = props.product.listPriceJPY
  const amounts = calculateRewardBreakdown({
    baseAmountJPY: saleAmount,
    superAgencyRate,
    agencyRate,
    connectorRate,
    assignedAgencyRole,
    hasSuperAgency: !!superAgency && assignedAgencyRole === '代理店',
    hasConnector,
  })

  return (
    <div className="grid gap-4">
      <div className="text-sm text-slate-700">
        <div className="font-semibold text-slate-900">{props.product.name}</div>
        <div className="text-xs text-slate-500">商談 ID: {props.deal.id}</div>
        <div className="mt-1 text-xs text-slate-500">
          担当先: {assignedAgency?.name ?? '未設定'}
          {connector ? ` / コネクター: ${connector.name}` : ' / コネクター: なし'}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-semibold text-slate-900">成約金額</div>
        {saleAmount > 0 ? (
          <div className="mt-2 text-sm text-slate-700">
            管理者が商品マスタで設定した金額で確定します。
            <div className="mt-1 text-base font-semibold text-slate-900">{formatJPY(saleAmount)}</div>
          </div>
        ) : (
          <div className="mt-2 text-sm text-amber-700">
            商品金額が未設定のため、この商談はまだ売上確定できません。管理者へ確認してください。
          </div>
        )}
      </div>

      <label className="grid gap-1">
        <span className="text-sm text-slate-700">売上確定日（YYYY-MM-DD）</span>
        <input
          value={closingDate}
          onChange={(event) => setClosingDate(event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
        />
      </label>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-semibold text-slate-900">配分プレビュー</div>
        <div className="mt-3 grid gap-2 text-sm text-slate-700">
          <div className="flex items-center justify-between">
            <span>報酬計算対象額</span>
            <span className="font-semibold">{formatJPY(amounts.baseAmountJPY)}</span>
          </div>
          {amounts.superAgencyRewardJPY > 0 ? (
            <div className="flex items-center justify-between">
              <span>
                {assignedAgencyRole === '総代理店' ? '総代理店報酬' : '総代理店報酬'}（{formatPercent(amounts.superAgencyAllocationRate)}）
              </span>
              <span className="font-semibold">{formatJPY(amounts.superAgencyRewardJPY)}</span>
            </div>
          ) : null}
          {amounts.agencyRewardJPY > 0 ? (
            <div className="flex items-center justify-between">
              <span>代理店報酬（{formatPercent(amounts.agencyAllocationRate)}）</span>
              <span className="font-semibold">{formatJPY(amounts.agencyRewardJPY)}</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <span>コネクター報酬（{hasConnector ? formatPercent(amounts.connectorAllocationRate) : '0%'}）</span>
            <span className="font-semibold">{formatJPY(amounts.connectorRewardJPY)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={props.onClose}>
          キャンセル
        </Button>
        <Button
          variant="primary"
          disabled={saleAmount <= 0}
          onClick={async () => {
            const result = await actions.finalizeDeal({
              dealId: props.deal.id,
              finalSaleAmountJPY: saleAmount,
              closingDate,
            })

            if (!result.ok) {
              toast.show(result.message ?? '売上確定できませんでした', 'error')
              return
            }

            toast.show('売上を確定しました', 'success')
            props.onClose()
          }}
        >
          売上確定する
        </Button>
      </div>
    </div>
  )
}

export default function DealsPage() {
  return (
    <ToastProvider>
      <DealsInner />
    </ToastProvider>
  )
}
